/**
 * ProductTilesContainer componet - holds shared functionality between tiles
 */

import React, { Component, Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _findIndex from 'lodash/findIndex';
import Slider from 'react-slick';

import { trackOnboardingStarted } from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';
import {
  BUSINESS_UNIT,
  COMPONENT,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';

// Actions
import { addToRegistry } from '../../../../actions/RegistryCollectionActions';
import { addItemToCart } from '../../../../actions/CartActions';
import {
  setOnboardAddToRegistryProductData,
  setOnboardAddToRegistryEventData,
  setOnboardRedirect,
  submitDefaultOnboarding,
} from '../../../../actions/OnboardActions';

// Helpers/Utils
import {
  customEventNames,
  trackEvent,
  trackProductAdded,
  trackProductClicked,
} from '../../../../util/trackingHelper';
import { getProductImage, getProductCategory } from '../../../../util/productHelper';
import FeatureFlags from '../../../../util/featureFlags';
import ProductTile from './ProductTile';
import defaultImage from '../../../../assets/images/no-image-available-300.png';
import './ProductTile.less';
import {
  buildCategoryChainFromKey,
  setBreadcrumbsStorage,
} from '../../../../pages/ProductDetailPage/helpers';

const OPEN_MODAL_EVENT = 'OPEN_MODAL_EVENT';

class ProductTilesContainer extends Component {
  constructor(props) {
    super(props);
    this.handleCartAdd = this.handleCartAdd.bind(this);
    this.handleRegistryAdd = this.handleRegistryAdd.bind(this);
    this.handleOpenPdp = this.handleOpenPdp.bind(this);
    this.addToRegistry = this.addToRegistry.bind(this);
    this.getEventRegistryItemAddedData = this.getEventRegistryItemAddedData.bind(this);
    if (props.activeCategoryKey && props.categoryTreeList) {
      this.categoryChain = buildCategoryChainFromKey(
        props.activeCategoryKey,
        props.categoryTreeList[0]
      );
    }
  }

  /**
   * Gets object with properties to rener productTile with
   * @param {Object} product - product object of type Merchandise
   */
  getProductProps(product) {
    const { hasCategoryLink } = this.props;
    const brand = product.brand && product.brand.name;
    const defaultProductLookId = product.default_product_look_id;
    const category = getProductCategory(product);
    return {
      productId: product.id,
      brand,
      hasCategoryLink,
      name: product.name,
      suggestedQuantity: product.suggested_quantity,
      productLooks: product.product_look_views,
      categoryView: category,
      swatches: product.swatches,
      defaultProductLookId,
      productShippingZones: product.shipping_zones,
      isPersonalizable: product.personalizable,
    };
  }

  /**
   * Getcsobject with properties to rener productTile with
   * @param {Object} product - product object of type KIT
   */
  getKitProps(product) {
    const { isRectangularImage } = this.props;
    const brand = product.brand && product.brand.name;
    const image =
      getProductImage(product, 'base', isRectangularImage) ||
      getProductImage(product, 'medium', isRectangularImage);
    return {
      isKit: true,
      brand,
      image,
      collectionKey: product.key,
      name: product.name,
      suggestedQuantity: product.suggested_quantity,
      swatches: product.swatches,
      priceRange: product.price_range,
      referencePrice: product.reference_price_range,
      productCount: product.product_count,
    };
  }

  /**
   * Returns proeprties to render the productTile with based on product type
   * @param {Object} product - product for which we want to get props
   */
  getProductTileProps(product) {
    if (product.product) {
      return this.getProductProps(product.product);
    }
    if (product.kit) {
      return this.getKitProps(product.kit);
    }
    if (product.collection_item) {
      return this.getProductProps(product.collection_item);
    }
    if (product.id) {
      return this.getProductProps(product);
    }
    return null;
  }

  getProductLookIndex(productLookId) {
    const { productLooks } = this.props;
    return Math.max(_findIndex(productLooks, { id: productLookId }), 0);
  }

  // TODO: investigate renaming eventData collectionName to kitKey in other files
  getPdpLink(kitKey, activeProductLook, eventData, categoryChain, partnerBoutique) {
    if (!eventData) {
      return kitKey ? `/shop/kit/${kitKey}` : `/shop/product/${activeProductLook.key}`;
    }

    let breadcrumbsParams;
    if (eventData.collection_key) {
      breadcrumbsParams = {
        ref_type: 'COLLECTION',
        ref_key: eventData.collection_key,
        ref_label: eventData.collection_name,
        product_look_key: activeProductLook && activeProductLook.key,
        kit_key: kitKey,
      };
    } else if (eventData.location === 'SEARCH') {
      breadcrumbsParams = {
        ref_type: 'SEARCH',
        search_term: eventData.search_term,
        product_look_key: activeProductLook && activeProductLook.key,
        kit_key: kitKey,
      };
    } else if (eventData.location === 'PARTNER_BOUTIQUE' && partnerBoutique) {
      breadcrumbsParams = {
        ref_type: 'PARTNER_BOUTIQUE',
        ref_key: partnerBoutique.brandKey,
        ref_label: partnerBoutique.brandName,
        product_look_key: activeProductLook && activeProductLook.key,
        kit_key: kitKey,
      };
    } else if (eventData.location === 'BRAND' && partnerBoutique) {
      breadcrumbsParams = {
        ref_type: 'BRAND',
        ref_key: partnerBoutique.brandKey,
        ref_label: partnerBoutique.brandName,
        product_look_key: activeProductLook && activeProductLook.key,
        kit_key: kitKey,
      };
    } else if (categoryChain) {
      breadcrumbsParams = {
        ref_type: 'CATEGORY_CHAIN',
        product_look_key: activeProductLook && activeProductLook.key,
        kit_key: kitKey,
      };
      categoryChain.forEach((cat, i) => {
        breadcrumbsParams[`ref_key_${i}`] = `${cat.key}|${cat.name}`;
      });
    }

    if (breadcrumbsParams) {
      setBreadcrumbsStorage(breadcrumbsParams);
    }
    return kitKey ? `/shop/kit/${kitKey}` : `/shop/product/${activeProductLook.key}`;
  }

  /**
   * Get transaction id relevant to the index clicked
   * @param {Number} index - absolute index of the tile clicked
   */
  getTransactionId(index) {
    const { transactionIdsMap } = this.props;
    const transactionIndex = _findIndex(
      Object.keys(transactionIdsMap),
      (key) => Number(key) >= index
    );
    return Object.values(transactionIdsMap)[transactionIndex];
  }

  getEventProductAddedData(item, index, quantity, skuPreview, activeProductLook) {
    const { eventData, transactionIdsMap } = this.props;
    const product = this.getProductTileProps(item);
    const imageUrl = activeProductLook
      ? getProductImage(activeProductLook, 'medium')
      : product.image;
    const productData = {
      quantity: quantity || product.productCount,
      image_url: imageUrl,
      brand: product.brand,
      name: product.name,
      position: index,
      product_id: product.productId || item.id,
    };
    if (skuPreview) {
      productData.price = (skuPreview.price_cents * skuPreview.quantity_multiplier) / 100;
      productData.sku = skuPreview.id;
    } else if (product.priceRange) {
      productData.price = Object.values(product.priceRange)
        .map((p) => p / 100)
        .join(' - ');
    }
    if (activeProductLook) {
      productData.variant = activeProductLook.id;
    }
    if (transactionIdsMap && FeatureFlags.get('passTransactionId')) {
      productData.transaction_id = this.getTransactionId(index);
    }
    return {
      ...productData,
      ...eventData,
    };
  }

  getEventRegistryItemAddedData({ index, item, quantity, skuPreview, activeProductLook }) {
    const { eventData, transactionIdsMap } = this.props;
    const product = this.getProductTileProps(item);
    const price = (skuPreview.price_cents * skuPreview.quantity_multiplier) / 100;
    const imageUrl = getProductImage(activeProductLook, 'medium');
    const productData = {
      brand: product.brand,
      image_url: imageUrl,
      name: product.name,
      price,
      quantity,
      sku: skuPreview.id,
    };
    if (transactionIdsMap && FeatureFlags.get('passTransactionId')) {
      productData.transaction_id = this.getTransactionId(index);
    }
    return {
      ...productData,
      ...eventData,
    };
  }

  redirectToOnboardWithProduct({ index, product, quantity, skuPreview, activeProductLook }) {
    const {
      history,
      dispatchSetOnboardAddToRegistryEventData,
      dispatchSetOnboardAddToRegistryProductData,
      dispatchSetOnboardRedirect,
    } = this.props;
    const skuId = skuPreview.id;
    const productData = { sku_id: skuId, quantity, enable_group_gifting: false };
    const eventData = this.getEventRegistryItemAddedData({
      index,
      item: product,
      quantity,
      skuPreview,
      activeProductLook,
    });
    dispatchSetOnboardAddToRegistryEventData(eventData);
    dispatchSetOnboardAddToRegistryProductData(productData);
    dispatchSetOnboardRedirect(window.location.pathname);
    history.push('/onboard/new');
  }

  /**
   * Handles following scenarios when adding item to registry:
   *  1. user has registry -> add to registry
   *  2. is not logged in -> propmpt sign up
   *  3. no registry but has wedding account -> reidrect to onboard
   *  4. logged in and no registry -> reidrect to onboard
   */
  handleRegistryAdd({
    index,
    product,
    quantity,
    skuPreview,
    categoryName,
    activeProductLook,
    productShippingZones,
  }) {
    const { userContext, dispatchSubmitDefaultRegistryOnboard } = this.props;
    if (userContext.has_registry) {
      return this.addToRegistry({
        index,
        product,
        quantity,
        skuPreview,
        categoryName,
        activeProductLook,
        productShippingZones,
      });
    }
    if (userContext.has_completed_onboarding) {
      trackOnboardingStarted(BUSINESS_UNIT.WEDDING_REGISTRY, COMPONENT.REGISTRY);
      return dispatchSubmitDefaultRegistryOnboard().then(() => {
        return this.addToRegistry({
          index,
          product,
          quantity,
          skuPreview,
          categoryName,
          activeProductLook,
          productShippingZones,
        });
      });
    }
    if (!userContext.has_registry) {
      this.redirectToOnboardWithProduct({
        index,
        product,
        quantity,
        skuPreview,
        activeProductLook,
      });
    }
    return null;
  }

  /**
   * Adds product to registry
   * @param {Object} product - product to add to registry
   * @param {Number} quantity - amount of this product to add to registry
   * @param {String} skuId - sku id of the product
   * @param {String} activeCategoryTitle - category name of the product
   * @param {Array<string>} productShippingZones - product shipping zones
   * */
  addToRegistry({
    index,
    product,
    quantity,
    skuPreview,
    categoryName,
    activeProductLook,
    productShippingZones,
  }) {
    const { onRegistryAdd } = this.props;
    const requestBody = {
      sku_id: skuPreview.id,
      enable_group_gifting: false,
      quantity,
    };
    return onRegistryAdd(requestBody, productShippingZones).then((response) => {
      const detail = {
        activeCategoryTitle: categoryName,
        skuId: skuPreview.id,
        collectionItemId: response.payload && response.payload.data.object_id,
        productId: response.payload && response.payload.data.product_id,
      };
      const data = this.getEventRegistryItemAddedData({
        index,
        item: product,
        quantity,
        skuPreview,
        activeProductLook,
      });
      trackEvent(customEventNames.REGISTRY_ITEM_ADDED, data);
      window.dispatchEvent(new CustomEvent('ADDED_ITEM_TO_REGISTRY', { detail }));
      return { added: true };
    });
  }

  /**
   * Adds item to cart, if product not in stock error message will be displayed
   * For items that hve lesser quantity in stock then entered, available quantity will be returned
   * @param {Object} product - product that is being added to cart
   * @param {Number} index - index of the tile on the page, used for tracking
   * @param {Number} quantity - product amount to be added
   * @param {Object} skuPreview - sku data for the product
   * @param {Object} activeProductLook - selected look for the product
   */
  handleCartAdd(product, index, quantity, skuPreview, activeProductLook) {
    const { onCartAdd } = this.props;
    const requestBody = {
      sku_id: skuPreview.id,
      quantity,
      item_origin: 'ECOMMERCE',
    };
    return onCartAdd(requestBody)
      .then((response) => {
        const cartId = response.payload.cart_id;
        const data = {
          ...this.getEventProductAddedData(product, index, quantity, skuPreview, activeProductLook),
          cart_id: cartId,
        };
        trackProductAdded(data);
        window.dispatchEvent(new CustomEvent(OPEN_MODAL_EVENT, { detail: 'CART' }));
      })
      .catch((response) => {
        if (
          response.category &&
          response.category.reason === 'sku-unavailable' &&
          response.trigger > 0
        ) {
          return { quantity: response.trigger };
        }
        return null;
      });
  }

  /**
   * Opens product detail page
   * @param {Object} product - product to be opened in PDP
   * @param {Number} index - index / position of the selected product
   * @param {Number} quantity - amount of the product set in qty field
   * @param {String} collectionKey - current collection key (if selecting KIT product)
   * @param {Object} skuPreview - sku data for the product
   * @param {Object} activeProductLook - selected look of a non KIT product
   */
  handleOpenPdp(product, index, quantity, collectionKey, skuPreview, activeProductLook) {
    const { onPdpRedirect } = this.props;
    if (FeatureFlags.get('enableProductClickedTracker')) {
      const data = this.getEventProductAddedData(
        product,
        index,
        quantity,
        skuPreview,
        activeProductLook
      );
      trackProductClicked(data);
    }

    if (onPdpRedirect) {
      onPdpRedirect(index);
    }
    if (collectionKey || activeProductLook) {
      const { eventData } = this.props;
      window.location.href = this.getPdpLink(collectionKey, activeProductLook, eventData);
    }
  }

  renderTilesWithSlider() {
    const {
      isRectangularImage,
      isBVSEOEnable,
      products,
      startIndex,
      eventData,
      fatProductUpdateCallback,
      isAddedStampVisibleOverride,
      sliderSettings,
    } = this.props;
    return (
      <Slider
        // eslint-disable-next-line
        ref={(c) => (this.slider = c)}
        className="products-slider"
        {...sliderSettings}
      >
        {products &&
          products.map((product, index) => (
            <ProductTile
              key={Math.random()}
              index={startIndex + index}
              isBVSEOEnable={isBVSEOEnable}
              isRectangularImage={isRectangularImage}
              isAddedStampVisibleOverride={
                isAddedStampVisibleOverride && isAddedStampVisibleOverride[index]
              }
              defaultImage={defaultImage}
              eventData={eventData}
              getPdpLink={this.getPdpLink}
              fatProductUpdateCallback={fatProductUpdateCallback}
              handleOpenPdp={(quantity, skuPreview, collectionKey, activeProductLook) => {
                this.handleOpenPdp(
                  product,
                  index,
                  quantity,
                  collectionKey,
                  skuPreview,
                  activeProductLook
                );
              }}
              onRegistryAdd={(
                quantity,
                skuPreview,
                categoryName,
                activeProductLook,
                productShippingZones
              ) =>
                this.handleRegistryAdd({
                  index,
                  product,
                  quantity,
                  skuPreview,
                  categoryName,
                  activeProductLook,
                  productShippingZones,
                })
              }
              onCartAdd={(quantity, skuPreview, activeProductLook) =>
                this.handleCartAdd(product, index, quantity, skuPreview, activeProductLook)
              }
              {...this.getProductTileProps(product)}
            />
          ))}
      </Slider>
    );
  }

  renderTiles() {
    const {
      tileWrapperCssClass,
      tilesSpacer,
      isRectangularImage,
      isBVSEOEnable,
      products,
      startIndex,
      eventData,
      isAddedStampVisibleOverride,
      fatProductUpdateCallback,
      fatHoverCallback,
      match,
      metaData,
      marketingModules,
      marketingModulesCallback,
    } = this.props;
    const partnerBoutique = {
      brandKey: match.params && match.params.brandKey,
      brandName: metaData && metaData.title,
    };

    return (
      products &&
      products.map((product, index) => {
        const currentTileIndex = startIndex + index;
        const showMarketingModule = marketingModules && marketingModules[currentTileIndex];
        const hoverListener =
          isAddedStampVisibleOverride && isAddedStampVisibleOverride[index]
            ? () => {
                fatHoverCallback(index);
              }
            : null;
        const tile = (
          <ProductTile
            defaultImage={defaultImage}
            eventData={eventData}
            partnerBoutique={partnerBoutique}
            index={currentTileIndex}
            isBVSEOEnable={isBVSEOEnable}
            isRectangularImage={isRectangularImage}
            getPdpLink={this.getPdpLink}
            categoryChain={this.categoryChain}
            handleOpenPdp={(quantity, skuPreview, collectionKey, activeProductLook) => {
              this.handleOpenPdp(
                product,
                index,
                quantity,
                collectionKey,
                skuPreview,
                activeProductLook
              );
            }}
            fatProductUpdateCallback={fatProductUpdateCallback}
            fatHoverCallback={hoverListener}
            isAddedStampVisibleOverride={
              isAddedStampVisibleOverride && isAddedStampVisibleOverride[index]
            }
            onRegistryAdd={(
              quantity,
              skuPreview,
              categoryName,
              activeProductLook,
              productShippingZones
            ) =>
              this.handleRegistryAdd({
                index,
                product,
                quantity,
                skuPreview,
                categoryName,
                activeProductLook,
                productShippingZones,
              })
            }
            onCartAdd={(quantity, skuPreview, activeProductLook) =>
              this.handleCartAdd(product, index, quantity, skuPreview, activeProductLook)
            }
            {...this.getProductTileProps(product)}
          />
        );
        return (
          <Fragment key={Math.random()}>
            {
              index > 0 &&
                tilesSpacer /*  so far tileSpacer (+ sign) is only used for FAT tiles display */
            }
            {showMarketingModule && marketingModules[currentTileIndex](marketingModulesCallback)}
            <div className={tileWrapperCssClass}>{tile}</div>
          </Fragment>
        );
      })
    );
  }

  render() {
    const { sliderSettings } = this.props;
    return sliderSettings ? this.renderTilesWithSlider() : this.renderTiles();
  }
}

ProductTilesContainer.defaultProps = {
  isBVSEOEnable: true,
};

ProductTilesContainer.propTypes = {
  isRectangularImage: PropTypes.bool,
  hasCategoryLink: PropTypes.bool,
  startIndex: PropTypes.number,
  tileWrapperCssClass: PropTypes.string,
  eventData: PropTypes.shape({}),
  products: PropTypes.arrayOf(PropTypes.shape({})),
  onPdpRedirect: PropTypes.func,
  // marketing modules
  marketingModules: PropTypes.shape({}),
  marketingModulesCallback: PropTypes.func,
  // for carousel view
  sliderSettings: PropTypes.shape({}),
  // helpers for FAT (frequently added together)
  tilesSpacer: PropTypes.element,
  isAddedStampVisibleOverride: PropTypes.arrayOf(PropTypes.bool),
  fatProductUpdateCallback: PropTypes.func,
  fatHoverCallback: PropTypes.func,
  // For adding when users does not have a registry
  dispatchSetOnboardAddToRegistryEventData: PropTypes.func,
  dispatchSetOnboardAddToRegistryProductData: PropTypes.func,
  dispatchSetOnboardRedirect: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  // tracking data for transaction ids for search plp
  transactionIdsMap: PropTypes.shape({}),
  // Bazaarvoice helper to disable SEO tracking for using tiles on the PDP
  isBVSEOEnable: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  userContext: state.user.userContext,
  registryInfo: state.registry,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onCartAdd: addItemToCart,
      onRegistryAdd: addToRegistry,
      dispatchSetOnboardAddToRegistryProductData: setOnboardAddToRegistryProductData,
      dispatchSetOnboardAddToRegistryEventData: setOnboardAddToRegistryEventData,
      dispatchSetOnboardRedirect: setOnboardRedirect,
      dispatchSubmitDefaultRegistryOnboard: submitDefaultOnboarding,
    },
    dispatch
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductTilesContainer));
